/**
 * S3 Uploads API
 *
 * Handles S3 upload watcher detections for tenant admins
 */
import helper from './helper'

export default {
  /**
   * Get watchers for the current organisation
   * @returns {Promise<Object>} { success, watchers }
   */
  async getWatchers() {
    return await helper.get('/api/s3-uploads/watchers')
  },

  /**
   * Create a new watcher for the current organisation
   * @param {Object} data - Watcher configuration
   * @param {number} data.storage_config_id - Storage config to watch
   * @param {string} data.watch_folder_path - Folder path to watch
   * @param {string} data.notification_emails - Comma-separated email addresses
   * @param {string} data.file_extensions - Comma-separated extensions (e.g., "mp4,mxf,mov")
   * @returns {Promise<Object>} { success, watcher }
   */
  async createWatcher(data) {
    return await helper.post('/api/s3-uploads/watchers', data)
  },

  /**
   * Update a watcher
   * @param {number} id - Watcher ID
   * @param {Object} data - Fields to update
   * @returns {Promise<Object>} { success, watcher }
   */
  async updateWatcher(id, data) {
    return await helper.put(`/api/s3-uploads/watchers/${id}`, data)
  },

  /**
   * Delete a watcher
   * @param {number} id - Watcher ID
   * @returns {Promise<Object>} { success }
   */
  async deleteWatcher(id) {
    return await helper.delete(`/api/s3-uploads/watchers/${id}`)
  },

  /**
   * Pre-seed existing files (prevent notification flood)
   * @param {number} id - Watcher ID
   * @returns {Promise<Object>} { success, seededCount }
   */
  async preSeedWatcher(id) {
    return await helper.post(`/api/s3-uploads/watchers/${id}/pre-seed`)
  },

  /**
   * Get detected uploads for the current organisation
   * @param {Object} params - Query parameters
   * @param {number} params.days - Number of days to look back (default 7)
   * @param {number} params.limit - Max results to return (default 200)
   * @returns {Promise<Object>} { success, detections, stats }
   */
  async getDetections(params = {}) {
    const queryParams = new URLSearchParams()
    if (params.days) queryParams.append('days', params.days)
    if (params.limit) queryParams.append('limit', params.limit)

    const queryString = queryParams.toString()
    return await helper.get(`/api/s3-uploads/detections${queryString ? '?' + queryString : ''}`)
  },

  /**
   * Link a detection to an episode or media
   * @param {number} id - Detection ID
   * @param {Object} data - Link data
   * @param {number} data.episode_id - Episode ID to link to
   * @param {number} data.media_id - Media ID to link to
   * @returns {Promise<Object>} { success }
   */
  async linkDetection(id, data) {
    return await helper.post(`/api/s3-uploads/detections/${id}/link`, data)
  }
}
